import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Send,
  Eye,
  CheckCircle,
  TrendingUp,
  Download,
  Target,
  AlertTriangle,
  FileText,
  Check,
  XCircle,
  Clock,
  Package,
  ShoppingCart,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import quotationDashboardService from '../../../api/crm/quotation-dashboard/quotationDashboardService';
import PermissionGate from '../../../components/common/PermissionGate';
import { getQuotationStatusClasses, formatQuotationStatus } from '../../../utils/quotationStatusUtils';
import ExportModal from '../../../components/common/ExportModal';
import { exportToCSV } from '../../../utils/exportUtils';
import { toast } from 'react-hot-toast';
import { formatIndianNumber } from '../../../utils/amountFormatter';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

const QuotationDashboard = () => {
  const navigate = useNavigate();

  // Loading states
  const [loadingKPIs, setLoadingKPIs] = useState(false);
  const [loadingQuotes, setLoadingQuotes] = useState(false);

  // KPI data
  const [kpis, setKpis] = useState({
    totalQuotations: 0,
    acceptedCount: 0,
    rejectedCount: 0,
    expiredCount: 0,
    sentCount: 0,
    seenCount: 0,
    draftCount: 0,
    expiringSoon: 0,
    orderCount: 0,
    orderedCount: 0,
    acceptanceRate: 0,
    quoteToOrderRate: 0
  });

  // Quote list data
  const [quotes, setQuotes] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 0
  });

  // Sorting state
  const [sortColumn, setSortColumn] = useState('created');
  const [sortDirection, setSortDirection] = useState('desc');

  // Selection & Export State
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // ── Date Filter State (same pattern as FieldSalesDashboard) ──────────────
  const [selectedPeriod, setSelectedPeriod] = useState('last_30_days');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Build the filter object passed to the API
  const dashboardFilters = useMemo(() => {
    const f = {};
    if (customDateRange.startDate || customDateRange.endDate) {
      if (customDateRange.startDate) f.startDate = customDateRange.startDate;
      if (customDateRange.endDate) f.endDate = customDateRange.endDate;
      f.dateRange = 'custom';
    } else {
      f.dateRange = selectedPeriod;
    }
    return f;
  }, [selectedPeriod, customDateRange]);

  // Fetch KPIs – re-runs when dashboardFilters changes
  useEffect(() => {
    const fetchKPIs = async () => {
      setLoadingKPIs(true);
      try {
        const result = await quotationDashboardService.getKPIs(dashboardFilters);
        if (result.success) {
          setKpis(result.kpis);
        }
      } catch (error) {

      } finally {
        setLoadingKPIs(false);
      }
    };

    fetchKPIs();
  }, [refreshKey, dashboardFilters]);

  // Fetch recent quotes – re-runs when dashboardFilters changes
  useEffect(() => {
    const fetchRecentQuotes = async () => {
      setLoadingQuotes(true);
      try {
        const result = await quotationDashboardService.getQuotationList(
          dashboardFilters,
          {
            tab: 'my-quotes',
            page: 1,
            limit: 20
          }
        );

        if (result.success) {
          // Filter out quotations with status "order" and "ordered"
          const filteredQuotes = result.quotations.filter(
            quote => quote.status !== 'order' && quote.status !== 'ordered'
          );

          // Sort by createdAt desc (most recent first)
          const sortedQuotes = filteredQuotes.sort((a, b) => {
            const dateA = new Date(a.createdAt || a.created_at || 0);
            const dateB = new Date(b.createdAt || b.created_at || 0);
            return dateB - dateA;
          });

          // Limit to 5 most recent quotes after filtering
          const limitedQuotes = sortedQuotes.slice(0, 5);
          setQuotes(limitedQuotes);
          setPagination({
            page: 1,
            limit: 5,
            totalRecords: limitedQuotes.length,
            totalPages: 1
          });
        }
      } catch (error) {

      } finally {
        setLoadingQuotes(false);
      }
    };

    fetchRecentQuotes();
  }, [refreshKey, dashboardFilters]);

  // Skeleton loading components
  const KPISkeleton = () => (
    <div className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
          <div className="h-8 bg-gray-200 rounded w-16"></div>
        </div>
        <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
      </div>
    </div>
  );

  const MetricSkeleton = () => (
    <div className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse">
      <div className="h-5 bg-gray-200 rounded w-32 mb-4"></div>
      <div className="h-10 bg-gray-200 rounded w-20"></div>
    </div>
  );

  const TableRowSkeleton = () => (
    <tr className="animate-pulse">
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-4"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
      <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded-full w-20"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
      <td className="px-6 py-4"><div className="h-8 bg-gray-200 rounded w-16"></div></td>
    </tr>
  );

  // Navigate to SendTrack detail page as per PRD Q0 requirement
  const handleViewQuote = (quote) => {
    const idToUse = quote._id || quote.quoteId;
    navigate(`/crm/quotation-proposal/send-track/${idToUse}`);
  };

  const handleSendQuote = (quote) => {
    const idToUse = quote._id || quote.quoteId;
    navigate(`/crm/quotation-proposal/send-track/${idToUse}`);
  };

  // Selection handlers
  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };

  const handleSelectAll = (items) => {
    if (items.length === 0) {
      setSelectedRows(new Set());
      return;
    }

    const allSelected = items.every(item => selectedRows.has(item._id || item.quoteId));

    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (allSelected) {
        items.forEach(item => newSelection.delete(item._id || item.quoteId));
      } else {
        items.forEach(item => newSelection.add(item._id || item.quoteId));
      }
      return newSelection;
    });
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    toast.success("Dashboard data refreshed");
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      handleExportCurrentPage();
    } else {
      setShowExportModal(true);
    }
  };

  const mapQuoteToExport = (quote) => {
    const createdDate = quote.createdAt || quote.created_at;
    const formattedDate = createdDate
      ? new Date(createdDate).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
      : 'N/A';

    return {
      'Quote ID': quote.quoteId || quote.quoteNumber || 'N/A',
      'Company': quote.companyName ? quote.companyName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : 'N/A',
      'Value': `₹${quote.value?.toLocaleString('en-IN') || '0'}`,
      'Status': formatQuotationStatus(quote.status),
      'Created': formattedDate
    };
  };

  const handleExportCurrentPage = () => {
    try {
      const dataToExport = selectedRows.size > 0
        ? quotes.filter(quote => selectedRows.has(quote._id || quote.quoteId))
        : quotes;

      if (dataToExport.length === 0) {
        toast.error('No data to export');
        return;
      }

      const type = selectedRows.size > 0 ? 'Selected Items' : 'Recent Quotes';
      const formattedData = dataToExport.map(mapQuoteToExport);
      const headers = ['Quote ID', 'Company', 'Value', 'Status', 'Created'];

      exportToCSV(formattedData, 'quotation_dashboard', { title: 'Quotation Dashboard', type }, headers);
      toast.success('Quotations exported successfully');
      setSelectedRows(new Set());
    } catch (error) {

      toast.error('Failed to export quotations');
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const result = await quotationDashboardService.getQuotationList(
        dashboardFilters,
        {
          tab: 'my-quotes',
          page: 1,
          limit: 1000
        }
      );

      if (result.success && result.quotations.length > 0) {
        const filteredQuotes = result.quotations.filter(
          quote => quote.status !== 'order' && quote.status !== 'ordered'
        );

        const mappedData = filteredQuotes.map(mapQuoteToExport);
        const headers = ['Quote ID', 'Company', 'Value', 'Status', 'Created'];

        exportToCSV(mappedData, 'quotations_all', { title: 'All Quotations', type: 'All Data' }, headers);
        toast.success(`Exported ${filteredQuotes.length} quotations successfully`);
        setShowExportModal(false);
      } else {
        toast.error('No data to export');
      }
    } catch (error) {

      toast.error('Failed to export quotations');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const result = await quotationDashboardService.getQuotationList(
        { startDate, endDate, dateRange: 'custom' },
        {
          tab: 'my-quotes',
          page: 1,
          limit: 1000
        }
      );

      if (result.success && result.quotations.length > 0) {
        const filteredQuotes = result.quotations.filter(quote => {
          if (quote.status === 'order' || quote.status === 'ordered') return false;

          const dateStr = quote.createdAt || quote.created_at;
          if (!dateStr) return false;

          try {
            const date = new Date(dateStr).toISOString().split('T')[0];
            return date >= startDate && date <= endDate;
          } catch (e) {
            return false;
          }
        });

        if (filteredQuotes.length === 0) {
          toast.error('No quotations found in the selected range');
          setExportLoading(false);
          return;
        }

        const mappedData = filteredQuotes.map(mapQuoteToExport);
        const headers = ['Quote ID', 'Company', 'Value', 'Status', 'Created'];
        const type = `Date Range (${startDate} to ${endDate})`;

        exportToCSV(mappedData, 'quotations_range', { title: 'Quotations', type }, headers);
        toast.success(`Exported ${filteredQuotes.length} quotations successfully`);
        setShowExportModal(false);
      } else {
        toast.error('No quotations found in the selected range');
      }
    } catch (error) {

      toast.error('Failed to export quotations');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted quotes for the table
  const getSortedQuotes = () => {
    if (!sortColumn || !quotes || quotes.length === 0) return quotes;

    return [...quotes].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'quoteId') {
        aValue = a.quoteId || a.quoteNumber || '';
        bValue = b.quoteId || b.quoteNumber || '';
      } else if (sortColumn === 'created') {
        aValue = new Date(a.createdAt || a.created_at || 0).getTime();
        bValue = new Date(b.createdAt || b.created_at || 0).getTime();
      } else if (sortColumn === 'value') {
        aValue = Number(a.value) || 0;
        bValue = Number(b.value) || 0;
      } else if (sortColumn === 'companyName') {
        aValue = a.companyName || '';
        bValue = b.companyName || '';
      } else if (sortColumn === 'status') {
        aValue = a.status || '';
        bValue = b.status || '';
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 !mt-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Monitor quote workload and performance across your team</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {/* Period selector — hidden when a custom date range is active */}
          {(!customDateRange.startDate && !customDateRange.endDate) && (
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all_time">All Time</option>
              <option value="today">Today</option>
              <option value="last_7_days">Last 7 days</option>
              <option value="last_30_days">Last 30 days</option>
              <option value="last_60_days">Last 60 days</option>
              <option value="last_90_days">Last 90 days</option>
            </select>
          )}

          <DateRangeFilter
            startDate={customDateRange.startDate}
            endDate={customDateRange.endDate}
            onChange={(range) => setCustomDateRange(range)}
            allowFutureDates={false}
          />

          {(customDateRange.startDate || customDateRange.endDate) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setCustomDateRange({ startDate: '', endDate: '' });
                setSelectedPeriod('last_30_days');
              }}
            >
              Clear
            </Button>
          )}

          <PermissionGate permission="quotation_management" action="read">
            <Button
              variant="outline"
              icon={<Download className="h-4 w-4" />}
              onClick={handleExportClick}
              className="w-full sm:w-auto h-10"
            >
              {selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {loadingKPIs ? (
          Array.from({ length: 10 }).map((_, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse flex-1 min-w-0">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-6 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))
        ) : (
          <>
            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">Quotations</p>
                  <p className="text-xl font-bold text-gray-900 truncate">{formatIndianNumber(kpis.totalQuotations)}</p>
                </div>
                <div className="p-2 bg-blue-100 rounded-full flex-shrink-0 ml-2">
                  <FileText className="h-5 w-5 text-[#0F2942]" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">Accepted</p>
                  <p className="text-xl font-bold text-green-600 truncate">{formatIndianNumber(kpis.acceptedCount)}</p>
                </div>
                <div className="p-2 bg-green-100 rounded-full flex-shrink-0 ml-2">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">Rejected</p>
                  <p className="text-xl font-bold text-red-600 truncate">{formatIndianNumber(kpis.rejectedCount)}</p>
                </div>
                <div className="p-2 bg-red-100 rounded-full flex-shrink-0 ml-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">Expired</p>
                  <p className="text-xl font-bold text-orange-600 truncate">{formatIndianNumber(kpis.expiredCount)}</p>
                </div>
                <div className="p-2 bg-orange-100 rounded-full flex-shrink-0 ml-2">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">Sent</p>
                  <p className="text-xl font-bold text-yellow-600 truncate">{formatIndianNumber(kpis.sentCount)}</p>
                </div>
                <div className="p-2 bg-yellow-100 rounded-full flex-shrink-0 ml-2">
                  <Send className="h-5 w-5 text-yellow-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">Seen</p>
                  <p className="text-xl font-bold text-purple-600 truncate">{formatIndianNumber(kpis.seenCount)}</p>
                </div>
                <div className="p-2 bg-purple-100 rounded-full flex-shrink-0 ml-2">
                  <Eye className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">Draft</p>
                  <p className="text-xl font-bold text-gray-600 truncate">{formatIndianNumber(kpis.draftCount)}</p>
                </div>
                <div className="p-2 bg-gray-100 rounded-full flex-shrink-0 ml-2">
                  <FileText className="h-5 w-5 text-gray-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">Expiring Soon</p>
                  <p className="text-xl font-bold text-amber-600 truncate">{formatIndianNumber(kpis.expiringSoon)}</p>
                </div>
                <div className="p-2 bg-amber-100 rounded-full flex-shrink-0 ml-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">Ready to Order</p>
                  <p className="text-xl font-bold text-indigo-600 truncate">{formatIndianNumber(kpis.orderCount)}</p>
                </div>
                <div className="p-2 bg-indigo-100 rounded-full flex-shrink-0 ml-2">
                  <Package className="h-5 w-5 text-indigo-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600 truncate">Ordered</p>
                  <p className="text-xl font-bold text-teal-600 truncate">{formatIndianNumber(kpis.orderedCount)}</p>
                </div>
                <div className="p-2 bg-teal-100 rounded-full flex-shrink-0 ml-2">
                  <ShoppingCart className="h-5 w-5 text-teal-600" />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {loadingKPIs ? (
          <>
            <MetricSkeleton />
            <MetricSkeleton />
          </>
        ) : (
          <>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Acceptance Rate</h3>
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-3xl font-bold text-green-600">
                {kpis.totalQuotations
                  ? ((kpis.acceptedCount / kpis.totalQuotations) * 100).toFixed(0)
                  : 0}%
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Quote→Order</h3>
                <Target className="h-5 w-5 text-purple-600" />
              </div>
              <div className="text-3xl font-bold text-purple-600">{kpis.quoteToOrderRate}%</div>
            </div>
          </>
        )}
      </div>

      {/* Recent Quotes Table */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Quotes</h2>
            {!loadingQuotes && quotes.length > 0 && (
              <Button
                variant="outline"
                onClick={() => navigate('/crm/quotation-proposal/create-quotes')}
              >
                View All
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>

                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('quoteId')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Quote ID</span>
                      {sortColumn === 'quoteId' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('companyName')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Company</span>
                      {sortColumn === 'companyName' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('value')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Value</span>
                      {sortColumn === 'value' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === 'status' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('created')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created</span>
                      {sortColumn === 'created' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingQuotes ? (
                  <>
                    {[...Array(5)].map((_, index) => (
                      <TableRowSkeleton key={index} />
                    ))}
                  </>
                ) : quotes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">
                      No recent quotes found
                    </td>
                  </tr>
                ) : (
                  getSortedQuotes().map((quote) => {
                    const createdDate = quote.createdAt || quote.created_at;
                    const formattedDate = createdDate
                      ? `${new Date(createdDate).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}, ${new Date(createdDate).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}`
                      : 'N/A';

                    return (
                      <tr key={quote._id || quote.quoteId} className="hover:bg-gray-50">

                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {quote.quoteId || quote.quoteNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize" title={quote.companyName || 'N/A'}>
                          {quote.companyName || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{quote.value?.toLocaleString('en-IN') || '0'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getQuotationStatusClasses(quote.status)}`}>
                            {formatQuotationStatus(quote.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formattedDate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <PermissionGate permission="quotation_management" action="read">
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<Eye className="h-4 w-4" />}
                              onClick={() => handleViewQuote(quote)}
                            >
                              View
                            </Button>
                          </PermissionGate>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        onExportRange={handleExportRange}
        loading={exportLoading}
        title="Export Quotations"
        showDateRange={true}
        allowFutureDates={true}
      />
    </div>
  );
};

export default QuotationDashboard;
